import { createContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'nls_currency';
const SUPPORTED = ['USD', 'GBP', 'EUR', 'AED'];
const DEFAULT_CURRENCY = 'USD';

const getInitialCurrency = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return SUPPORTED.includes(stored) ? stored : DEFAULT_CURRENCY;
};

/** @type {import('react').Context<{ currency: string, setCurrency: (code: string) => void, supportedCurrencies: string[] }>} */
export const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  supportedCurrencies: SUPPORTED,
});

/**
 * Provides the selected currency to all children.
 * Persists the selection to localStorage so it survives page reload.
 * The axios interceptor in axiosConfig.js reads localStorage directly so
 * it stays in sync without an additional context consumer.
 */
export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(getInitialCurrency);

  const setCurrency = useCallback((/** @type {string} */ code) => {
    if (!SUPPORTED.includes(code)) return;
    localStorage.setItem(STORAGE_KEY, code);
    setCurrencyState(code);
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, supportedCurrencies: SUPPORTED }}>
      {children}
    </CurrencyContext.Provider>
  );
};
